/**
 * When the undo bar should go before its eight seconds are up. An undo is worked out from the
 * day as it was when the action went out. If the day has moved on since (the round ended, a new
 * one started, someone else paused or resumed the hall) it would land somewhere else: a minute
 * taken off the wrong clock, a hall paused again that another admin had just resumed.
 */

import type { Phase, TournamentEvent, TournamentState } from "../../../../engine/types";

/** The parts of the day an undo leans on, as they stand once the action has landed. */
export interface UndoMoment {
  phase: Phase;
  roundNo: number | null;
  pausedAll: boolean;
  /** False while the action is itself a pause or a resume that has not come back from the server yet. */
  landed: boolean;
}

/** The moment an action leaves behind: the day as it is, with the pause as the action sets it. */
export function momentAfter(before: TournamentState, event: TournamentEvent): UndoMoment {
  const pausedAll = event.type === "pause.all" ? event.on : before.pausedAll;
  return { phase: before.clock.phase, roundNo: before.clock.roundNo, pausedAll, landed: pausedAll === before.pausedAll };
}

/** The same moment once `state` has been seen: a pause or resume of our own counts as landed when it shows. */
export function seen(moment: UndoMoment, state: TournamentState): UndoMoment {
  return !moment.landed && state.pausedAll === moment.pausedAll ? { ...moment, landed: true } : moment;
}

/** True when the day has moved on from the moment the bar was shown, so the bar should go. */
export function movedOn(moment: UndoMoment, state: TournamentState): boolean {
  if (state.clock.phase !== moment.phase || state.clock.roundNo !== moment.roundNo) return true;
  return moment.landed && state.pausedAll !== moment.pausedAll;
}
